import {UseFormSetValue, useFormContext} from 'react-hook-form'
import {useTranslation} from '@/translations/i18n.tsx'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {MailerFormValues} from '@/features/mailer/types'
import {MailerMultiSelect} from '@/features/mailer/components/mailer-multi-select/MailerMultiSelect'
import {useGetUserSpecializations} from '@/features/users/queries/useGetUserSpecializations'
import {StyledFieldsGrid} from '@/features/mailer/components/patient-filters-panel/style'

// Staff ("Personale") filters per functional spec §5. The "Gruppo" select is
// an addition over the design doc: the BE shipped the `group` filter key
// specifically for this redesign (OPSSTD-3643 §4).
export const ConsultantFiltersPanel = () => {
    const {t} = useTranslation()
    const {watch, setValue} = useFormContext<MailerFormValues>()

    const values = watch()

    const specializationsQuery = useGetUserSpecializations({limit: 100})
    const specializationOptions = specializationsQuery.remappedData.map(specialization => ({
        value: specialization.id,
        label: specialization.name
    }))

    // Public signature is fully typed (UseFormSetValue); the internal cast works
    // around RHF's conditional PathValue types not distributing through wrappers.
    const setField: UseFormSetValue<MailerFormValues> = (name, value) =>
        setValue(name, value as never, {shouldValidate: true, shouldDirty: true})

    return (
        <StyledFieldsGrid $columns={2}>
            <InputSelect
                label={t('mailer:staff_status_label')}
                value={values.consultantStatus}
                onChange={value => setField('consultantStatus', value)}
                optionsMap={{
                    all: 'mailer:staff_status_all',
                    operative: 'mailer:staff_status_operative',
                    candidate: 'mailer:staff_status_candidate',
                    suspended: 'mailer:staff_status_suspended',
                    absent: 'mailer:staff_status_absent',
                    terminated: 'mailer:staff_status_terminated'
                }}
            />
            <InputSelect
                label={t('mailer:staff_group_label')}
                value={values.consultantGroup}
                onChange={value => setField('consultantGroup', value)}
                optionsMap={{
                    all: 'mailer:staff_group_all',
                    referring_doctor: 'mailer:staff_group_referring_doctor',
                    therapist: 'mailer:staff_group_therapist'
                }}
            />
            <InputSelect
                label={t('mailer:contract_type_label')}
                value={values.consultantContractType}
                onChange={value => setField('consultantContractType', value)}
                optionsMap={{
                    all: 'mailer:contract_type_all',
                    consultant: 'mailer:contract_type_consultant',
                    flat_rate: 'mailer:contract_type_flat_rate',
                    employee: 'mailer:contract_type_employee'
                }}
            />
            <MailerMultiSelect
                label={t('mailer:specialization_label')}
                placeholder={t('mailer:specialization_placeholder')}
                value={values.consultantSpecializationIds}
                onChange={value => setField('consultantSpecializationIds', value)}
                options={specializationOptions}
            />
            <InputSelect
                label={t('mailer:work_sector_label')}
                value={values.consultantWorkSector}
                onChange={value => setField('consultantWorkSector', value)}
                optionsMap={{
                    all: 'mailer:work_sector_all',
                    domiciliary: 'mailer:work_sector_domiciliary',
                    outpatient: 'mailer:work_sector_outpatient'
                }}
            />
        </StyledFieldsGrid>
    )
}

ConsultantFiltersPanel.displayName = 'ConsultantFiltersPanel'
